/**
 * CmdK — Phase 2.5 omnibar.
 *
 * Press Cmd+K (Ctrl+K on non-Mac) anywhere in the console; a centered
 * palette opens. Type a free-form query, press Enter, and Claude
 * answers via /api/ask — streaming text + a visible tool-call trace
 * (each tool fires shows name, args, latency, result preview).
 *
 * Recent queries persist in localStorage so re-opening the palette
 * gives instant access to the last 8.
 *
 * Architecture mirrors ExplainButton.ts: a global single-instance
 * drawer mounted once in App.ts, lit-html re-renders on every state
 * change.
 */
import { html, render, type TemplateResult } from "lit-html";
import { setActiveTab } from "../App";
import { setBetInspectorFilters } from "./BetInspector";

const RECENT_KEY  = "kitchen-console:recent-asks";
const MAX_RECENTS = 8;

interface ToolCall {
  id:        string;
  name:      string;
  input:     unknown;
  result?:   string;          // preview string from worker
  full_size?: number;
  ms?:       number;
  error?:    string | null;
  state:     "running" | "done" | "error";
}

interface Turn {
  q:      string;
  text:   string;                                // accumulated assistant text
  tools:  ToolCall[];
  error:  Error | null;
  done:   { hops: number; input_tokens: number; output_tokens: number } | null;
}

interface AskState {
  open:    boolean;
  query:   string;
  running: boolean;
  abort:   AbortController | null;
  recents: string[];
  turns:   Turn[];                                // single source of truth
  paint:   () => void;
}

const state: AskState = {
  open:    false,
  query:   "",
  running: false,
  abort:   null,
  recents: loadRecents(),
  turns:   [],
  paint:   () => {},
};

// =============================================================
// Mount — call once in App.ts
// =============================================================
export function renderCmdK(root: HTMLElement): void {
  state.paint = () => render(template(), root);
  state.paint();

  document.addEventListener("keydown", (e) => {
    const isMod = e.metaKey || e.ctrlKey;
    if (isMod && e.key.toLowerCase() === "k") {
      e.preventDefault();
      open();
    } else if (e.key === "Escape" && state.open) {
      e.preventDefault();
      close();
    }
  });
}

function open(): void {
  state.open = true;
  state.paint();
  // Focus the input on the next frame
  queueMicrotask(() => {
    const inp = document.getElementById("cmdk-input") as HTMLInputElement | null;
    inp?.focus();
    inp?.select();
  });
}

function close(): void {
  cancel();
  state.open = false;
  state.paint();
}

function cancel(): void {
  state.abort?.abort();
  state.abort = null;
  state.running = false;
}

// =============================================================
// Render
// =============================================================
function template(): TemplateResult {
  if (!state.open) return html``;

  return html`
    <div class="cmdk-backdrop"
         @click=${(e: MouseEvent) => {
           if (e.target === e.currentTarget) close();
         }}>
      <div class="cmdk-palette panel--hero" role="dialog" aria-label="Console assistant">
        ${header()}
        ${input()}
        ${state.turns.length === 0 ? recentsView() : transcript()}
      </div>
    </div>
  `;
}

function header(): TemplateResult {
  // Show usage badge if the most recent turn finished
  const last = state.turns.at(-1);
  const usage = last?.done
    ? `${last.done.hops} hops · ${(last.done.input_tokens + last.done.output_tokens).toLocaleString()} tok`
    : null;

  return html`
    <div class="cmdk-header">
      <span class="cmdk-header__icon">⌘K</span>
      <span class="cmdk-header__label">ask the console</span>
      <span class="cmdk-header__spacer"></span>
      ${usage ? html`<span class="cmdk-header__usage">${usage}</span>` : ""}
      ${state.turns.length > 0 ? html`
        <button class="cmdk-header__clear"
                @click=${() => { state.turns = []; state.paint(); }}
                title="Clear conversation">clear</button>
      ` : ""}
      <button class="cmdk-header__close"
              @click=${close}
              title="Close (Esc)">×</button>
    </div>
  `;
}

function input(): TemplateResult {
  return html`
    <form class="cmdk-input-row"
          @submit=${(e: Event) => { e.preventDefault(); submit(); }}>
      <span class="cmdk-input-row__prompt">${state.running ? "…" : "›"}</span>
      <input
        id="cmdk-input"
        class="cmdk-input"
        type="text"
        placeholder=${randomPlaceholder()}
        autocomplete="off"
        spellcheck="false"
        .value=${state.query}
        ?disabled=${state.running}
        @input=${(e: Event) => {
          state.query = (e.target as HTMLInputElement).value;
          state.paint();   // re-render so the ask button's disabled
                            // attribute tracks whether the input is empty
        }}
      />
      ${state.running ? html`
        <button type="button" class="cmdk-input-row__cancel"
                @click=${cancel}>cancel</button>
      ` : html`
        <button type="submit" class="cmdk-input-row__submit"
                ?disabled=${!state.query.trim()}>↵ ask</button>
      `}
    </form>
  `;
}

function recentsView(): TemplateResult {
  if (state.recents.length === 0) {
    return html`
      <div class="cmdk-empty">
        <p style="margin: 0; font-size: 12px; color: var(--text-tertiary);">
          ask anything about the pipeline:
        </p>
        <ul class="cmdk-suggestions">
          ${SUGGESTIONS.map((s) => html`
            <li>
              <button class="cmdk-suggestion"
                      @click=${() => { state.query = s; submit(); }}>
                ${s}
              </button>
            </li>
          `)}
        </ul>
      </div>
    `;
  }
  return html`
    <div class="cmdk-recents">
      <h4>recent</h4>
      <ul>
        ${state.recents.map((q) => html`
          <li>
            <button class="cmdk-recent-item"
                    @click=${() => { state.query = q; submit(); }}>
              <span class="cmdk-recent-item__icon">↻</span>
              <span>${q}</span>
            </button>
          </li>
        `)}
      </ul>
    </div>
  `;
}

function transcript(): TemplateResult {
  return html`
    <div class="cmdk-transcript">
      ${state.turns.map((t, i) => renderTurn(t, i === state.turns.length - 1))}
    </div>
  `;
}

function renderTurn(t: Turn, isLast: boolean): TemplateResult {
  const isStreaming = isLast && state.running;
  return html`
    <div class="cmdk-turn ${isLast ? "" : "cmdk-turn--past"}">
      <div class="cmdk-turn__q">${t.q}</div>

      ${t.tools.length > 0 ? html`
        <details class="cmdk-tools" ?open=${isStreaming || hasErroredTool(t)}>
          <summary>
            <span>${toolsSummary(t)}</span>
            <span class="cmdk-tools__chev">▾</span>
          </summary>
          <ol class="cmdk-tools__list">
            ${t.tools.map(toolRow)}
          </ol>
        </details>
      ` : ""}

      ${t.text ? html`
        <div class="cmdk-answer">
          ${renderInlineMd(t.text)}
          ${isStreaming ? html`<span class="cmdk-caret"></span>` : ""}
        </div>
      ` : isStreaming ? html`
        <div class="cmdk-answer cmdk-answer--shimmer">
          thinking<span class="cmdk-dots">...</span>
        </div>
      ` : ""}

      ${t.error ? html`
        <div class="cmdk-error">
          <strong>error</strong> ${t.error.message}
        </div>
      ` : ""}
    </div>
  `;
}

function toolRow(t: ToolCall): TemplateResult {
  const cls = t.state === "error" ? "is-error"
            : t.state === "done"  ? "is-done"
            : "is-running";
  return html`
    <li class="cmdk-tool ${cls}">
      <header class="cmdk-tool__head">
        <span class="cmdk-tool__icon">${t.state === "running" ? "○" : t.state === "error" ? "×" : "✓"}</span>
        <code class="cmdk-tool__name">${t.name}</code>
        <code class="cmdk-tool__args">${formatArgs(t.input)}</code>
        <span class="cmdk-tool__spacer"></span>
        ${t.ms != null ? html`<span class="cmdk-tool__ms">${t.ms}ms</span>` : ""}
        ${t.full_size != null ? html`<span class="cmdk-tool__size">${fmtBytes(t.full_size)}</span>` : ""}
      </header>
      ${t.error ? html`
        <pre class="cmdk-tool__error">${t.error}</pre>
      ` : t.result ? html`
        <pre class="cmdk-tool__result">${t.result}</pre>
      ` : ""}
      ${deepLinkRow(t)}
    </li>
  `;
}

/** Render a "open in <tab> →" affordance under tool calls whose result
 *  maps cleanly to a console tab. Each branch is responsible for
 *  syncing its target tab's view-state before the route flip. */
function deepLinkRow(t: ToolCall): TemplateResult | "" {
  if (t.state !== "done") return "";

  if (t.name === "query_bets") {
    const f = (t.input ?? {}) as { sport?: string; outcome?: string; search?: string };
    return html`
      <div class="cmdk-tool__deeplink">
        <button class="cmdk-tool__deeplink-btn"
                @click=${() => {
                  const filters: { sport?: string; outcome?: string; search?: string } = {};
                  if (f.sport)   filters.sport   = f.sport;
                  if (f.outcome) filters.outcome = f.outcome;
                  if (f.search)  filters.search  = f.search;
                  setBetInspectorFilters(filters);
                  setActiveTab("bets");
                  close();
                }}>
          Open in Bet Inspector <span class="cmdk-tool__deeplink-arrow">→</span>
        </button>
      </div>
    `;
  }

  if (t.name === "calibration_summary") {
    return html`
      <div class="cmdk-tool__deeplink">
        <button class="cmdk-tool__deeplink-btn"
                @click=${() => { setActiveTab("calibration"); close(); }}>
          Open Calibration Lab <span class="cmdk-tool__deeplink-arrow">→</span>
        </button>
      </div>
    `;
  }

  if (t.name === "git_log") {
    return html`
      <div class="cmdk-tool__deeplink">
        <button class="cmdk-tool__deeplink-btn"
                @click=${() => { setActiveTab("rewind"); close(); }}>
          Open Rewind <span class="cmdk-tool__deeplink-arrow">→</span>
        </button>
      </div>
    `;
  }

  return "";
}

function toolsSummary(turn: Turn): string {
  const done   = turn.tools.filter((t) => t.state === "done").length;
  const errs   = turn.tools.filter((t) => t.state === "error").length;
  const total  = turn.tools.length;
  const totalMs = turn.tools.reduce((s, t) => s + (t.ms ?? 0), 0);
  return `${total} tool call${total === 1 ? "" : "s"}` +
         (errs > 0 ? ` · ${errs} error${errs === 1 ? "" : "s"}` : "") +
         ` · ${totalMs}ms` +
         (done < total ? " (running)" : "");
}

function hasErroredTool(turn: Turn): boolean {
  return turn.tools.some((t) => t.state === "error");
}

// =============================================================
// Submit + stream parsing
// =============================================================
async function submit(): Promise<void> {
  const q = state.query.trim();
  if (!q || state.running) return;

  // Push a new empty turn — every state update from here on writes
  // into this turn directly. Single source of truth.
  const turn: Turn = {
    q, text: "", tools: [], error: null, done: null,
  };
  state.turns.push(turn);
  state.query = "";
  state.running = true;
  saveRecent(q);
  state.recents = loadRecents();
  state.paint();

  const ac = new AbortController();
  state.abort = ac;

  try {
    const r = await fetch("/api/ask", {
      method:  "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body:    JSON.stringify({ query: q }),
      signal:  ac.signal,
      redirect: "error",
    });
    if (!r.ok) {
      const errBody = await r.text().catch(() => "");
      throw new Error(`/api/ask → ${r.status}: ${errBody.slice(0, 200)}`);
    }
    if (!r.body) throw new Error("no_response_body");

    const reader  = r.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) handleFrame(frame, turn);
    }
  } catch (e) {
    if ((e as Error).name === "AbortError") return;
    turn.error = e instanceof Error ? e : new Error(String(e));
  } finally {
    state.running = false;
    state.abort   = null;
    state.paint();
  }
}

function handleFrame(frame: string, turn: Turn): void {
  const lines = frame.split("\n");
  let event = "";
  let dataLine = "";
  for (const l of lines) {
    if (l.startsWith("event:")) event = l.slice(6).trim();
    else if (l.startsWith("data:")) dataLine = l.slice(5).trim();
  }
  if (!event || !dataLine) return;

  let data: any;
  try { data = JSON.parse(dataLine); } catch { return; }

  switch (event) {
    case "text":
      turn.text += data.text ?? "";
      break;
    case "tool_use":
      turn.tools.push({
        id: data.id, name: data.name, input: data.input,
        state: "running",
      });
      break;
    case "tool_result": {
      const t = turn.tools.find((x) => x.id === data.id);
      if (t) {
        t.result    = data.preview;
        t.full_size = data.full_size;
        t.ms        = data.ms;
        t.error     = data.error ?? null;
        t.state     = data.error ? "error" : "done";
      }
      break;
    }
    case "done":
      turn.done = {
        hops: data.hops,
        input_tokens: data.input_tokens,
        output_tokens: data.output_tokens,
      };
      break;
    case "error":
      turn.error = new Error(data.message ?? "unknown error");
      break;
  }
  state.paint();
}

// =============================================================
// Helpers
// =============================================================
function loadRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    return (JSON.parse(raw) as string[]).slice(0, MAX_RECENTS);
  } catch { return []; }
}
function saveRecent(q: string): void {
  try {
    const cur = loadRecents().filter((x) => x !== q);
    cur.unshift(q);
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, MAX_RECENTS)));
  } catch { /* noop */ }
}

function formatArgs(input: unknown): string {
  if (!input || typeof input !== "object") return "";
  const entries = Object.entries(input as Record<string, unknown>);
  if (entries.length === 0) return "()";
  return "(" + entries.map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(", ") + ")";
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

function randomPlaceholder(): string {
  return SUGGESTIONS[Math.floor(Math.random() * SUGGESTIONS.length)] ?? "";
}

// Tiny markdown renderer — bold / italic / code / line breaks
function renderInlineMd(text: string): TemplateResult[] {
  return text.split("\n").map((line) => {
    const segs: TemplateResult[] = [];
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) segs.push(html`${line.slice(last, m.index)}`);
      const tok = m[0];
      if      (tok.startsWith("**")) segs.push(html`<strong>${tok.slice(2, -2)}</strong>`);
      else if (tok.startsWith("*"))  segs.push(html`<em>${tok.slice(1, -1)}</em>`);
      else                            segs.push(html`<code>${tok.slice(1, -1)}</code>`);
      last = re.lastIndex;
    }
    if (last < line.length) segs.push(html`${line.slice(last)}`);
    return html`<div class="cmdk-line">${segs}</div>`;
  });
}

const SUGGESTIONS = [
  "what shipped today across both repos",
  "how is the calibration looking right now",
  "any failed launchagents in the last 24 hours",
  "why did soccer fixture refire so often today",
  "show me MLB bets that lost yesterday",
  "how did the WNBA picks do yesterday",
  "is the VPS scraper healthy",
  "summarize the worst-calibrated bucket",
  "which strategy_id has the best CLV",
];
